import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import { IconAlert } from '../icons/icons';
import { cn } from '../lib/cn';
const BASE = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-[background-color,border-color,color,opacity] ease-brand disabled:cursor-not-allowed disabled:opacity-50';
const SIZE = {
    sm: 'min-h-[32px] px-3.5 text-[12.5px]',
    md: 'min-h-[48px] px-6 text-[15px]',
    lg: 'min-h-[54px] px-8 text-[16px]',
};
/*
 * Every variant carries a border so switching between them never shifts
 * layout; primary and ghost keep it transparent. Primary's colours are the
 * accent tokens rather than `bg-lila` so the tokens file stays the one place
 * that says what the action accent is. The outline variants use `--ink`,
 * which flips with the theme, so they stay navy-on-light and amande-on-navy.
 */
const VARIANT = {
    primary: {
        base: 'border border-transparent bg-accent text-on-accent',
        hover: 'hover:bg-accent-hover',
    },
    secondary: {
        base: 'border border-ink bg-transparent text-ink',
        hover: 'hover:bg-cream-dark',
    },
    ghost: {
        base: 'border border-transparent bg-transparent text-ink',
        hover: 'hover:bg-cream-dark',
    },
    destructive: {
        base: 'border border-ink bg-transparent text-ink',
        hover: 'hover:bg-error-surface hover:text-ink-on-tint hover:border-transparent',
    },
};
export function Button({ variant = 'primary', size = 'md', fullWidth = false, loading = false, loadingLabel, asChild = false, className, children, disabled, type, ...rest }) {
    const inert = disabled || loading;
    const classes = cn(BASE, SIZE[size], VARIANT[variant].base, !inert && VARIANT[variant].hover, fullWidth && 'w-full', className);
    const content = (_jsxs(_Fragment, { children: [variant === 'destructive' ? _jsx(IconAlert, { size: 18, title: null }) : null, loading && loadingLabel !== undefined ? loadingLabel : children] }));
    if (asChild) {
        /*
         * Slot needs exactly one element child and hands it the classes. A link
         * cannot be `disabled` and a loading link is a contradiction, so neither
         * reaches the child; the destructive icon is not injected either — a
         * destructive action is a button, not a navigation.
         */
        return (_jsx(Slot, { className: classes, ...rest, children: children }));
    }
    return (_jsx("button", { 
        /* Never a submit button by accident: buttons sit inside forms. */
        type: type ?? 'button', className: classes, disabled: inert, "aria-busy": loading || undefined, ...rest, children: content }));
}
//# sourceMappingURL=button.js.map